<template>
  <transition name="modal-fade" @after-leave="afterLeave">
    <div v-if="isVisible" class="modal-backdrop" @click="handleBackdropClick">
      <transition name="modal-slide">
        <div class="modal" :class="[props?.size && `modal--size-${props?.size}`]" @click.stop>
          <div class="modal__heading">
            <div v-if="props.title" class="modal__title">
              {{ props.title }}
            </div>
            <BaseIcon
              v-if="isClosable"
              @click="handleCloseClick"
              name="xmark"
              size="16"
            />
          </div>
          <div class="modal__body" :class="{ 'modal__body--contained': props?.bodyScroll === false }">
            <p v-if="props?.description" class="mb-4">{{ props?.description }}</p>
            <div v-if="showReasonInput && !hasComponent" class="modal__reason">
              <RejectReasonPicker
                v-if="reasonPresets.length"
                :model-value="reasonCode"
                :options="reasonPresets"
                :label="props?.reasonLabel"
                :error="reasonError"
                @update:model-value="onReasonCodeChange"
              />
              <TextAreaInput
                id="modal-reason-input"
                :label="reasonTextLabel"
                :placeholder="props?.reasonPlaceholder || ''"
                :model-value="reasonText"
                :rows="reasonPresets.length ? 3 : 4"
                @update:model-value="reasonText = $event"
              />
            </div>
            <slot></slot>
          </div>
          <div v-if="props?.onSubmit && !hasComponent" class="modal__actions">
            <Btn
              v-if="props?.onSubmit && !hasComponent && !props?.hideCancel"
              :text="props?.cancelText ?? $t('common.cancel')"
              @click="handleCancel"
              variant="outlined"
              :disabled="props?.loading"
            />
            <Btn
              :text="props?.submitText ?? $t('common.confirm')"
              @click="handleSubmit"
              :variant="buttonVariant"
              :loading="props?.loading"
            />
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
  import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import RejectReasonPicker from '@shared/components/proposal/RejectReasonPicker.vue';
  import { formatRejectReason } from '@shared/constants/proposalRejectReasons.js';

  export default {
    name: 'ModalEl',
    components: {
      BaseIcon,
      Btn,
      TextAreaInput,
      RejectReasonPicker,
    },
    props: {
      onClose: {
        type: Function,
        required: false,
      },
      onSubmit: {
        type: Function,
        required: false,
      },
      submitText: {
        type: String,
        default: null,
      },
      show: {
        type: Boolean,
        default: false,
      },
      props: {
        type: Object,
      },
      hasComponent: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['after-leave', 'close'],
    setup(props, { emit, slots }) {
      const isVisible = ref(props.show);
      const hasDefaultSlot = computed(() => {
        return slots.default && slots.default().length > 0;
      });

      const buttonVariant = computed(() => {
        const status = props.props?.status;
        const submitVariant = props.props?.submitVariant;

        // If submitVariant is explicitly provided, use it
        if (submitVariant) {
          return submitVariant;
        }

        // Otherwise, map status to variant
        if (status === 'danger') {
          return 'danger';
        }
        if (status === 'info') {
          return 'secondary';
        }
        return status || 'secondary';
      });

      const isClosable = computed(() => props.props?.closable !== false);
      // Opt-in: backdrop click only closes when caller passes closeOnBackdrop: true.
      const closeOnBackdrop = computed(() => props.props?.closeOnBackdrop === true);

      const handleEscape = (e) => {
        if (e.key === 'Escape' && isVisible.value && isClosable.value) {
          requestClose();
        }
      };

      const requestClose = async () => {
        const beforeClose = props.props?.beforeClose;
        if (typeof beforeClose === 'function') {
          const result = beforeClose();
          const allowed = result && typeof result.then === 'function' ? await result : result;
          if (allowed) closeModal();
        } else {
          closeModal();
        }
      };

      const handleBackdropClick = () => {
        if (!isClosable.value) return;
        if (!closeOnBackdrop.value) return;
        requestClose();
      };
      const handleCloseClick = () => requestClose();
      const handleCancel = () => requestClose();

      onMounted(() => {
        document.addEventListener('keydown', handleEscape);
      });

      onUnmounted(() => {
        document.removeEventListener('keydown', handleEscape);
      });

      watch(
        () => props.show,
        (newVal) => {
          isVisible.value = newVal;
        }
      );
      const closeModal = () => {
        emit('close');
      };

      const afterLeave = () => {
        emit('after-leave');
      };

      const reasonText = ref('');
      const reasonCode = ref('');
      const reasonError = ref('');

      const showReasonInput = computed(() => Boolean(props.props?.showReasonInput));
      const reasonPresets = computed(() =>
        Array.isArray(props.props?.reasonPresets) ? props.props.reasonPresets : []
      );
      const reasonTextLabel = computed(() => {
        if (reasonPresets.value.length) {
          return props.props?.reasonNoteLabel || '';
        }
        return props.props?.reasonLabel || '';
      });

      const resetReasonState = () => {
        reasonText.value = '';
        reasonCode.value = '';
        reasonError.value = '';
      };

      const onReasonCodeChange = (value) => {
        reasonCode.value = value;
        reasonError.value = '';
      };

      watch(
        () => props.show,
        (visible) => {
          if (visible) resetReasonState();
        }
      );

      const handleSubmit = () => {
        if (!props.props?.onSubmit) return;

        if (showReasonInput.value) {
          if (props.props?.reasonRequired && !reasonCode.value && reasonPresets.value.length) {
            reasonError.value = props.props?.reasonRequiredMessage || '';
            return;
          }

          const selected = reasonPresets.value.find((option) => option.value === reasonCode.value);
          const reason = formatRejectReason({
            code: reasonCode.value,
            note: reasonText.value,
            label: selected?.label,
          });

          props.props.onSubmit(reason, { reasonCode: reasonCode.value || null });
          return;
        }

        props.props.onSubmit();
      };

      return {
        closeModal,
        isVisible,
        afterLeave,
        handleSubmit,
        hasDefaultSlot,
        buttonVariant,
        requestClose,
        handleBackdropClick,
        handleCloseClick,
        handleCancel,
        isClosable,
        showReasonInput,
        reasonPresets,
        reasonText,
        reasonCode,
        reasonError,
        reasonTextLabel,
        onReasonCodeChange,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/mixins';
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(17, 24, 39, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    overflow: hidden;
    padding: 2rem;
    // Safe area insets for PWA
    padding-top: calc(2rem + env(safe-area-inset-top, 0px));
    padding-bottom: calc(2rem + env(safe-area-inset-bottom, 0px));
    padding-left: calc(2rem + env(safe-area-inset-left, 0px));
    padding-right: calc(2rem + env(safe-area-inset-right, 0px));
    @include lg-down {
      padding: 1rem;
      padding-top: calc(1rem + env(safe-area-inset-top, 0px));
      padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
      padding-left: calc(1rem + env(safe-area-inset-left, 0px));
      padding-right: calc(1rem + env(safe-area-inset-right, 0px));
    }
  }

  .modal {
    background: white;
    border-radius: 0.5rem;
    max-width: 500px;
    max-height: calc(100vh - 4rem);
    overflow: hidden;
    width: 100%;
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      0 10px 15px -3px rgb(0 0 0 / 0.1),
      0 4px 6px -4px rgb(0 0 0 / 0.1);
    display: flex;
    flex-direction: column;
    &--size- {
      &sm {
        max-width: 350px;
      }
      // Fits the weekly opening-hours card (22.75rem) plus the 1.5rem body padding
      &compact {
        max-width: 25.75rem;
      }
      &md {
        max-width: 500px;
      }
      &lg {
        max-width: 700px;
      }
      &xl {
        max-width: 1000px;
      }
      &xxl {
        max-width: 1400px;
      }
    }
    &__title {
      font-size: 1.125rem;
      font-weight: 600;
    }
    &__heading {
      border-bottom: 1px solid var(--slate-200);
      padding: 1rem 1.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
      .icon {
        color: var(--slate-400);
        @include hover {
          color: var(--slate-700);
        }
      }
    }
    &__body {
      padding: 1rem 1.5rem;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      @include lg-down {
        padding: 1rem;
      }
      p {
        line-height: 1.5;
        margin-bottom: 0;
      }
    }

    &__body--contained {
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    &__reason {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin-bottom: 0.25rem;
    }

    &__actions {
      padding: 1rem 1.5rem;
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      flex-shrink: 0;

      .btn:only-child {
        margin-left: auto; // This will push a single button to the right
      }
    }
  }

  .modal-fade-enter-active .modal-backdrop,
  .modal-fade-leave-active .modal-backdrop {
    opacity: 1;
  }

  .modal-fade-enter-active .modal,
  .modal-fade-leave-active .modal {
    transform: translateY(0);
  }
</style>
